import { createStyles } from 'antd-style';

import {
  ThemeBorderRadius,
  ThemeColors,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../../theme';

const SCROLLBAR_WIDTH = 8;
const RESERVE_ON_SIDES = 20;

// NewShipmentPage.jsx / CopyShipmentPage.jsx apply this directly (via
// `className`) to the one `InnerContentScrollbars` instance that always
// renders there, so this is compound with `verticalOutside` (`&.<marker>`).
// It's a plain string, not a hash: it's only ever used nested inside
// `verticalOutside`'s own rule below, never as a standalone top-level key —
// `createStyles` only treats a top-level key as a style *name* (see
// `detailPageMarker` below for why that matters), so a raw selector string
// only compiles correctly when nested inside a real style's object.
export const SCROLLBAR_NARROW_TRACK_OFFSET_MARKER =
  'scrollbar-narrow-track-offset';

export const useScrollbarsStyles = createStyles(({ css, cx, token }) => {
  // Fully hashed — no literal `Scrollbar-*` class is rendered anymore.
  // Transfer.styles.js, NonLoggedPage.styles.js, addressBook.styles.js and
  // MonitorJobMap.styles.js receive these hashes via `createStyles(fn, props)`
  // (call `useScrollbarsStyles()` in the owning component, pass its `styles`
  // as the second arg to their own style hook).
  const view = cx(
    css({
      // Firefox
      scrollbarWidth: "'none'",
      // IE10
      msOverflowStyle: "'-ms-autohiding-scrollbar'",
      // Others
      '& *::-webkit-scrollbar': {
        display: "'none'",
      },
    })
  );
  const thumb = cx(
    css({
      backgroundColor: ThemeColors.COLOR_GREY_BLUISH4,
      cursor: 'pointer',
      borderRadius: ThemeBorderRadius.BORDER_RADIUS_MD,
    })
  );
  const trackVertical = cx(
    css({
      right: ThemeSpaces.SPACE_NORM0_5,
      bottom: 0,
      top: 0,
      borderRadius: 0,
      width: `${SCROLLBAR_WIDTH}px !important`,
    })
  );
  // No external consumer — fully hashed, no literal string needed.
  const trackHorizontal = cx(
    css({
      right: 0,
      left: 0,
      borderRadius: 0,
      height: `${SCROLLBAR_WIDTH}px !important`,
    })
  );
  // Passed in via `className` by CustomGroupAutocomplete.jsx / TemplatesListPopup.jsx.
  const themeLight = cx(css({}));
  // Applied by DetailPage.jsx to its `-InnerContent` div — an ancestor of
  // whichever descendant Scrollbars instance ends up rendering there (it
  // isn't always the same component), so its own content has to hold the
  // `& .${thumb}` descendant rule directly, rather than being referenced
  // from inside some unrelated top-level style (that would force the wrong
  // element to be the ancestor).
  const detailPageMarker = cx(
    css({
      [`& .${thumb}`]: {
        [`@media (max-width: ${token.screenSMMax}px)`]: {
          zIndex: 2,
        },
      },
    })
  );

  return {
    view,
    thumb,
    trackVertical,
    trackHorizontal,
    themeLight,
    detailPageMarker,
    // Applied alongside the literal `Scrollbar` root class — carries the
    // marker/theme-driven overrides that need to reach into descendants.
    scrollbar: {
      [`&.${themeLight} .${thumb}`]: {
        backgroundColor: hexToRgbaWithAlpha(
          ThemeColors.COLOR_GREY_BLUISH4,
          0.5
        ),
      },
    },
    noHorizontal: {
      [`> .${view}`]: {
        overflowY: 'scroll',
        overflowX: 'hidden !important',
        marginBottom: '0 !important',
      },
      [`> .${trackHorizontal}`]: {
        visibility: 'hidden !important',
      },
    },
    spaceOutside: {
      // This is a reserve for shadows and other graphical content on the edge
      marginLeft: -RESERVE_ON_SIDES,
      marginRight: -RESERVE_ON_SIDES,

      [`> .${view}`]: {
        paddingLeft: RESERVE_ON_SIDES,
        paddingRight: RESERVE_ON_SIDES,
      },
      [`> .${trackVertical}`]: {
        right: RESERVE_ON_SIDES,
      },
    },
    shrinkContent: {
      [`> .${view}`]: {
        paddingRight: SCROLLBAR_WIDTH + ThemeSpaces.SPACE_XS,
      },
    },
    verticalOutside: {
      [`> .${trackVertical}`]: {
        [`@media (min-width: ${token.screenMDMin}px)`]: {
          right: RESERVE_ON_SIDES - SCROLLBAR_WIDTH - 6,
        },
        [`@media (max-width: ${token.screenSMMax}px)`]: {
          right: RESERVE_ON_SIDES - SCROLLBAR_WIDTH - 2,
        },
      },
      [`&.${SCROLLBAR_NARROW_TRACK_OFFSET_MARKER}, .${detailPageMarker} &`]: {
        [`> .${trackVertical}`]: {
          [`@media (max-width: ${token.screenMDMax}px)`]: {
            right: RESERVE_ON_SIDES - SCROLLBAR_WIDTH - 2,
          },
        },
      },
    },
    horizontalTop: {
      [`> .${trackHorizontal}`]: {
        top: 0,
      },
    },
    horizontalBottom: {
      [`> .${trackHorizontal}`]: {
        bottom: 0,
      },
    },
  };
});
